// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plane, Hotel, Car, Users, AlertTriangle, DollarSign } from "lucide-react";
import { useUpcomingEventsForTravel, useStaffTravelStatus, useStaffAssignmentsForEvent } from "@/hooks/useTravelData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const bookingStatusColors: Record<string, string> = {
  needed: "bg-red-500 text-white",
  requested: "bg-yellow-500 text-white",
  booked: "bg-blue-500 text-white",
  confirmed: "bg-green-500 text-white",
  cancelled: "bg-gray-400 text-white",
};

const TravelBoardTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForTravel();
  const queryClient = useQueryClient();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: travelStatus, isLoading: tsLoading } = useStaffTravelStatus(selectedEventId);
  const { data: assignedStaff } = useStaffAssignmentsForEvent(selectedEventId);

  // Quick-edit modal state
  const [editModal, setEditModal] = useState<{ open: boolean; staffId: string; staffName: string; type: string; existing: any } | null>(null);
  const [editForm, setEditForm] = useState({ provider: "", departure_city: "", arrival_city: "", departure_date: "", return_date: "", confirmation_number: "", cost: "", booking_status: "needed", payment_method: "company_card", notes: "" });

  // Home location modal
  const [homeModal, setHomeModal] = useState<{ open: boolean; staffId: string; staffName: string } | null>(null);
  const [homeForm, setHomeForm] = useState({ city: "", state: "", nearest_airport: "", notes: "" });

  const assignedStaffIds = useMemo(() => new Set((assignedStaff || []).map((a: any) => a.staff_id)), [assignedStaff]);

  const filteredStatus = useMemo(() => {
    if (!travelStatus) return [];
    return travelStatus.filter((s: any) => assignedStaffIds.has(s.staff_id));
  }, [travelStatus, assignedStaffIds]);

  // Summaries
  const totalTravelers = filteredStatus.length;
  const flightsBooked = filteredStatus.filter((s: any) => s.flight_status === "booked" || s.flight_status === "confirmed").length;
  const flightsNeeded = filteredStatus.filter((s: any) => s.flight_status === "needed" || !s.flight_status).length;
  const hotelsBooked = filteredStatus.filter((s: any) => s.hotel_status === "booked" || s.hotel_status === "confirmed").length;
  const hotelsNeeded = filteredStatus.filter((s: any) => s.hotel_status === "needed" || !s.hotel_status).length;
  const carsBooked = filteredStatus.filter((s: any) => s.car_status === "booked" || s.car_status === "confirmed").length;
  const carsNeeded = filteredStatus.filter((s: any) => s.car_status === "needed" || !s.car_status).length;
  const totalSpend = filteredStatus.reduce((sum: number, s: any) => sum + (Number(s.flight_cost) || 0) + (Number(s.hotel_cost) || 0) + (Number(s.car_cost) || 0), 0);

  const handleStatusClick = (staffId: string, staffName: string, type: string, status: string, provider: string, cost: number) => {
    setEditForm({
      provider: provider || "",
      departure_city: "",
      arrival_city: "",
      departure_date: "",
      return_date: "",
      confirmation_number: "",
      cost: cost?.toString() || "",
      booking_status: status || "needed",
      payment_method: "company_card",
      notes: "",
    });
    setEditModal({ open: true, staffId, staffName, type, existing: null });
  };

  const handleSaveBooking = async () => {
    if (!editModal || !selectedEventId) return;
    const payload = {
      event_id: selectedEventId,
      staff_id: editModal.staffId,
      travel_type: editModal.type,
      provider: editForm.provider || null,
      departure_city: editForm.departure_city || null,
      arrival_city: editForm.arrival_city || null,
      departure_date: editForm.departure_date || null,
      return_date: editForm.return_date || null,
      confirmation_number: editForm.confirmation_number || null,
      cost: editForm.cost ? parseFloat(editForm.cost) : null,
      booking_status: editForm.booking_status,
      payment_method: editForm.payment_method || null,
      notes: editForm.notes || null,
    };

    // Check if booking exists for this staff/event/type
    const { data: existing } = await supabase
      .from("manage_travel_bookings")
      .select("id")
      .eq("event_id", selectedEventId)
      .eq("staff_id", editModal.staffId)
      .eq("travel_type", editModal.type)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase.from("manage_travel_bookings").update(payload).eq("id", existing.id);
      if (error) { toast.error("Failed to update booking"); return; }
      toast.success("Booking updated");
    } else {
      const { error } = await supabase.from("manage_travel_bookings").insert(payload);
      if (error) { toast.error("Failed to create booking"); return; }
      toast.success("Booking created");
    }
    setEditModal(null);
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["travel-bookings", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["travel-bookings-all"] });
    queryClient.invalidateQueries({ queryKey: ["travel-summary", selectedEventId] });
  };

  const handleSaveHome = async () => {
    if (!homeModal) return;
    const payload = {
      staff_id: homeModal.staffId,
      city: homeForm.city,
      state: homeForm.state || null,
      nearest_airport: homeForm.nearest_airport || null,
      notes: homeForm.notes || null,
    };
    const { error } = await supabase.from("manage_staff_home_locations").upsert(payload, { onConflict: "staff_id" });
    if (error) { toast.error("Failed to save location"); return; }
    toast.success("Home location saved");
    setHomeModal(null);
    queryClient.invalidateQueries({ queryKey: ["staff-travel-status", selectedEventId] });
    queryClient.invalidateQueries({ queryKey: ["staff-home-locations"] });
  };

  const renderStatusBadge = (status: string | null, staffId: string, staffName: string, type: string, provider: string, cost: number) => {
    const s = status || "needed";
    return (
      <Badge
        className={`${bookingStatusColors[s] || bookingStatusColors.needed} cursor-pointer`}
        onClick={() => handleStatusClick(staffId, staffName, type, s, provider, cost)}
      >
        {s}
      </Badge>
    );
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="min-w-[280px]">
        <Label>Select Event</Label>
        <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
          <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
          <SelectContent>
            {(events || []).map((e: any) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name} ({format(parseLocalDate(e.start_date), "MMM d")} - {format(parseLocalDate(e.end_date), "MMM d, yyyy")})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEventId && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              <div><p className="text-xl font-bold">{totalTravelers}</p><p className="text-xs text-muted-foreground">Travelers</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Plane className="h-6 w-6 text-blue-500" />
              <div><p className="text-xl font-bold">{flightsBooked} / {flightsBooked + flightsNeeded}</p><p className="text-xs text-muted-foreground">Flights</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Hotel className="h-6 w-6 text-purple-500" />
              <div><p className="text-xl font-bold">{hotelsBooked} / {hotelsBooked + hotelsNeeded}</p><p className="text-xs text-muted-foreground">Hotels</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Car className="h-6 w-6 text-green-500" />
              <div><p className="text-xl font-bold">{carsBooked} / {carsBooked + carsNeeded}</p><p className="text-xs text-muted-foreground">Cars</p></div>
            </CardContent></Card>
            <Card className="col-span-2"><CardContent className="p-3 flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-secondary" />
              <div><p className="text-xl font-bold">${totalSpend.toLocaleString()}</p><p className="text-xs text-muted-foreground">Total Spend</p></div>
            </CardContent></Card>
          </div>

          {/* Travel Board Table */}
          {tsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Staff Name</TableHead>
                        <TableHead>Home City</TableHead>
                        <TableHead>Airport</TableHead>
                        <TableHead>Flight</TableHead>
                        <TableHead>Hotel</TableHead>
                        <TableHead>Car</TableHead>
                        <TableHead className="text-right">Total Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStatus.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No assigned staff found for this event.</TableCell></TableRow>
                      ) : filteredStatus.map((s: any) => {
                        const total = (Number(s.flight_cost) || 0) + (Number(s.hotel_cost) || 0) + (Number(s.car_cost) || 0);
                        const hasHome = s.home_city;
                        return (
                          <TableRow key={s.staff_id}>
                            <TableCell className="font-medium">{s.staff_name}</TableCell>
                            <TableCell>
                              {hasHome ? (
                                <span>{s.home_city}, {s.home_state}</span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-yellow-600 text-xs p-0 h-auto"
                                  onClick={() => { setHomeForm({ city: "", state: "", nearest_airport: "", notes: "" }); setHomeModal({ open: true, staffId: s.staff_id, staffName: s.staff_name }); }}
                                >
                                  ⚠️ Set Home City
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{s.nearest_airport || "—"}</TableCell>
                            <TableCell>{renderStatusBadge(s.flight_status, s.staff_id, s.staff_name, "flight", s.flight_provider, s.flight_cost)}</TableCell>
                            <TableCell>{renderStatusBadge(s.hotel_status, s.staff_id, s.staff_name, "hotel", s.hotel_provider, s.hotel_cost)}</TableCell>
                            <TableCell>{renderStatusBadge(s.car_status, s.staff_id, s.staff_name, "rental_car", s.car_provider, s.car_cost)}</TableCell>
                            <TableCell className="text-right font-medium">{total > 0 ? `$${total.toLocaleString()}` : "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Quick-Edit Booking Modal */}
      <Dialog open={!!editModal?.open} onOpenChange={(v) => !v && setEditModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editModal?.staffName} — {editModal?.type === "rental_car" ? "Rental Car" : editModal?.type?.charAt(0).toUpperCase() + (editModal?.type?.slice(1) || "")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Status *</Label>
              <Select value={editForm.booking_status} onValueChange={(v) => setEditForm({ ...editForm, booking_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="needed">Needed</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="booked">Booked</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Provider</Label><Input value={editForm.provider} onChange={(e) => setEditForm({ ...editForm, provider: e.target.value })} placeholder="e.g. Delta, Marriott..." /></div>
            {(editModal?.type === "flight" || editModal?.type === "rental_car") && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label>From City</Label><Input value={editForm.departure_city} onChange={(e) => setEditForm({ ...editForm, departure_city: e.target.value })} /></div>
                <div><Label>To City</Label><Input value={editForm.arrival_city} onChange={(e) => setEditForm({ ...editForm, arrival_city: e.target.value })} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Departure</Label><Input type="date" value={editForm.departure_date} onChange={(e) => setEditForm({ ...editForm, departure_date: e.target.value })} /></div>
              <div><Label>Return</Label><Input type="date" value={editForm.return_date} onChange={(e) => setEditForm({ ...editForm, return_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Confirmation #</Label><Input value={editForm.confirmation_number} onChange={(e) => setEditForm({ ...editForm, confirmation_number: e.target.value })} /></div>
              <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} /></div>
            </div>
            <div><Label>Payment Method</Label>
              <Select value={editForm.payment_method} onValueChange={(v) => setEditForm({ ...editForm, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company_card">Company Card</SelectItem>
                  <SelectItem value="reimbursement">Reimbursement</SelectItem>
                  <SelectItem value="comped">Comped</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
            <Button onClick={handleSaveBooking} className="w-full">Save Booking</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Home Location Modal */}
      <Dialog open={!!homeModal?.open} onOpenChange={(v) => !v && setHomeModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Set Home Location — {homeModal?.staffName}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>City *</Label><Input value={homeForm.city} onChange={(e) => setHomeForm({ ...homeForm, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={homeForm.state} onChange={(e) => setHomeForm({ ...homeForm, state: e.target.value })} /></div>
            <div><Label>Nearest Airport Code</Label><Input value={homeForm.nearest_airport} onChange={(e) => setHomeForm({ ...homeForm, nearest_airport: e.target.value })} placeholder="e.g. MCI, ORD" /></div>
            <div><Label>Notes</Label><Textarea value={homeForm.notes} onChange={(e) => setHomeForm({ ...homeForm, notes: e.target.value })} /></div>
            <Button onClick={handleSaveHome} className="w-full">Save Location</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TravelBoardTab;
